/**
 * Centralized message filtering utility for UI-level message filtering
 * This ensures "Session Complete" and other unwanted messages are filtered consistently across the app
 */

export interface MessageFilterConfig {
  blockedMessageTypes: string[];
  blockedMessageContent: string[];
  blockedNotificationTypes: string[];
}

// Default configuration for blocked messages
const DEFAULT_FILTER_CONFIG: MessageFilterConfig = {
  blockedMessageTypes: [
    'SessionComplete',
    'SessionCompleted', 
    'session-complete',
    'session-completed'
  ],
  blockedMessageContent: [
    'session complete',
    'session completed',
    'sessioncomplete',
    'sessioncompleted',
    'charging session complete',
    'charging session completed',
    'session has completed',
    'session has finished',
    'session finished',
    'session ended',
    'session ending',
    'session done',
    'charge complete',
    'charge completed',
    'charging complete',
    'charging completed',
    'charging finished',
    'charging ended'
  ],
  blockedNotificationTypes: [
    'SessionComplete',
    'SessionCompleted',
    'ChargingComplete',
    'ChargingCompleted'
  ]
};

/**
 * Checks if a message should be filtered (blocked) based on content analysis
 */
export function shouldFilterMessage(
  message: any,
  config: MessageFilterConfig = DEFAULT_FILTER_CONFIG
): boolean {
  if (!message) return false;

  // Explicit allowlist: the charger activity feed (client/src/pages/ActivityPage.tsx) needs to
  // see every event, including "charging completed" / "session ended" style entries that this
  // filter exists to hide from the Live Updates drawer. Never let those content checks below
  // swallow an ActivityEvent message.
  if (message.type === 'ActivityEvent') {
    return false;
  }

  // Extract all possible message content and type fields
  const messageText = message.data?.Message || message.data?.message || message.message || '';
  const messageType = message.type || message.data?.Type || message.data?.type || '';
  const notificationType = message.data?.Metadata?.NotificationType || 
                          message.data?.NotificationType || 
                          message.notificationType || '';
  const status = message.data?.status || message.status || '';
  
  // Convert all fields to lowercase for case-insensitive comparison
  const textFields = [messageText, messageType, notificationType, status]
    .filter(field => field && typeof field === 'string')
    .map(field => field.toLowerCase());

  // Check message types
  for (const blockedType of config.blockedMessageTypes) {
    if (textFields.some(field => field === blockedType.toLowerCase())) {
      return true;
    }
  }

  // Check message content
  for (const blockedContent of config.blockedMessageContent) {
    if (textFields.some(field => field.includes(blockedContent.toLowerCase()))) {
      return true;
    }
  }

  // Check notification types
  for (const blockedNotificationType of config.blockedNotificationTypes) {
    if (textFields.some(field => field === blockedNotificationType.toLowerCase())) {
      return true;
    }
  }

  return false;
}

/**
 * Enhanced session complete detection with comprehensive patterns
 */
export function isSessionCompleteMessage(message: any): boolean {
  const sessionCompleteConfig: MessageFilterConfig = {
    blockedMessageTypes: [],
    blockedMessageContent: [
      'session complete',
      'session completed', 
      'session has been completed',
      'charging session complete',
      'charging session completed',
      'charging session has completed',
      'charging complete',
      'charging completed',
      'charge complete',
      'charge completed',
      'session finished',
      'session ended',
      'charging finished',
      'charging ended'
    ],
    blockedNotificationTypes: [
      'SessionComplete',
      'SessionCompleted',
      'ChargingComplete',
      'ChargingCompleted'
    ]
  };

  return shouldFilterMessage(message, sessionCompleteConfig);
}

/**
 * Logs filtered messages for debugging purposes
 */
export function logFilteredMessage(message: any, reason: string): void {
  console.log(`🚫 MESSAGE FILTERED: ${reason}`);
  console.log('🚫 Filtered message details:', {
    type: message.type,
    dataType: message.data?.Type,
    notificationType: message.data?.Metadata?.NotificationType,
    message: message.data?.Message || message.data?.message,
    status: message.data?.status,
    fullMessage: message
  });
}

/**
 * Safe message content extractor that handles various message formats
 */
export function extractMessageContent(message: any): {
  text: string;
  type: string;
  notificationType: string;
  status: string;
} {
  return {
    text: message.data?.Message || message.data?.message || message.message || '',
    type: message.type || message.data?.Type || message.data?.type || '',
    notificationType: message.data?.Metadata?.NotificationType || 
                     message.data?.NotificationType || 
                     message.notificationType || '',
    status: message.data?.status || message.status || ''
  };
}